/**
 * @fileoverview Acciones para la gestión y carga de etiquetas (tags) en el inbox.
 * Proporciona funciones para obtener, establecer y manejar errores de tags usando el modelo TagsModelMC.
 * @module actions/inbox/tagActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 3.1.0
 */

/**
 * @typedef {Object} Tag
 * @description Estructura de datos para una etiqueta (tag).
 * @property {string|number} id - Identificador único de la etiqueta.
 * @property {string} name - Nombre de la etiqueta.
 * @property {string} [color] - Color asociado a la etiqueta.
 * @property {boolean} [active] - Estado de actividad de la etiqueta.
 */

import TagsModelMC from '../../presentation/models/TagsModelMC';
import { TAGS_ACTIONS } from '../../constants/actionsConstants';

/**
 * Acción asíncrona para obtener todas las etiquetas siempre que no estén en estado de carga.
 *
 * @function getTagsAlways
 * @category Actions Layer
 * @param {Object} params - Parámetros para la consulta de etiquetas.
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @example
 * await getTagsAlways({ active: true })(dispatch, getState);
 * @see {@link TagsModelMC.get}
 */
export const getTagsAlways = (params) => async (dispatch, getState) => {
  const { loading } = getState().tagsReducer;
  if (loading) return;
  dispatch({ type: 'TAGS_LOADING_START' });
  try {
    const data = await TagsModelMC.get(params);
    dispatch({ type: TAGS_ACTIONS.SET_ALL_TAGS, data });
  } catch (err) {
    dispatch({ type: 'TAGS_LOADING_ERROR', error: err.message || 'Error loading tags' });
  }
};

/**
 * Acción asíncrona para obtener etiquetas solo si el array está vacío y no se está cargando.
 *
 * @function getTagsIfEmpty
 * @category Actions Layer
 * @param {Object} params - Parámetros para la consulta de etiquetas.
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @example
 * await getTagsIfEmpty({ active: true })(dispatch, getState);
 * @see {@link TagsModelMC.get}
 */
export const getTagsIfEmpty = (params) => async (dispatch, getState) => {
  const { tags, loading } = getState().tagsReducer;
  if (loading) return;
  if (!tags.length) {
    dispatch({ type: 'TAGS_LOADING_START' });
    try {
      const data = await TagsModelMC.get(params);
      dispatch({ type: TAGS_ACTIONS.SET_ALL_TAGS, data });
    } catch (err) {
      dispatch({ type: 'TAGS_LOADING_ERROR', error: err.message || 'Error loading tags' });
    }
  } else {
    dispatch({ type: 'TAGS_ALREADY_LOADED' });
  }
};
